import { OnlineSales, OnlineSalesUnauthenticated, RpcClientFactory } from 'gw-edge-client';
import { selector } from 'recoil';
import { type JobInfoDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto';
import { topConfigContextAtom } from '../atoms/topConfigContextAtom';
import { jobIdAtom } from '../atoms/jobIdAtom';
import { getPrimaryAccountNumber } from '../../utils/topConfigContextUtil';
import type { ProductName_Top } from 'gw-edge-client/dist/types/dto/pc/typekey';
import { businessFlowSelector } from './getBusinessFlowSelector';
import { mpsActivationInfoAtom } from '../atoms/mpsActivationInfoAtom';
import { isAxiosError } from 'axios';
import { resumeOnsaAtom } from '../atoms/resumeOnsaAtom';
import { wait } from '../../utils/asyncUtil';

const RETRY_TIMEOUT = 2000;
const MAX_RETRIES = 10;

export const jobInfoSelector = selector<JobInfoDTO>({
    key: 'jobInfoSelector',
    get: async ({ get }) => {
        let jobId = get(jobIdAtom);
        const businessFlow = get(businessFlowSelector);
        const mpsPolicyNumber = get(mpsActivationInfoAtom).policeNummer;
        const topConfigContext = get(topConfigContextAtom);
        const resumeOnsa = get(resumeOnsaAtom);
        const isLoggedOn = topConfigContext?.user?.isLoggedOn;
        const accountNumber = getPrimaryAccountNumber(topConfigContext);
        // Using URL search parmas instead of opcInfoAtom to avoid side effect when opcInfoAtom is updated
        const urlSearchParams = new URLSearchParams(window.location.search);
        const policeNummer = urlSearchParams.get('policeNummer');
        const coverableId = urlSearchParams.get('coverableId');
        const opcProduct = urlSearchParams.get('productName') as ProductName_Top;

        if (businessFlow === 'MPS') {
            jobId = await OnlineSales.getLatesBoundTransactionIdByPolicyNumber(mpsPolicyNumber);
        }

        const isAccountMissingAfterResumeInOnsa = (err: Error): boolean => {
            return (
                !!resumeOnsa.jobNumber &&
                isAxiosError(err) &&
                err.response?.data.error.pureMessage_Top === 'No Account and no Contact found'
            );
        };

        let retries = 0;
        let jobInfo;
        while (!jobInfo && retries < MAX_RETRIES) {
            try {
                jobInfo = await OnlineSalesUnauthenticated.getOrCreateJob(
                    isLoggedOn ? accountNumber : '',
                    jobId ?? '',
                    policeNummer ?? '',
                    !!resumeOnsa.jobNumber,
                    coverableId ?? '',
                    opcProduct,
                    !isLoggedOn
                );
            } catch (err) {
                if (isAccountMissingAfterResumeInOnsa(err as Error)) {
                    await wait(RETRY_TIMEOUT);
                } else {
                    throw err;
                }
            }
            retries++;
        }
        if (!jobInfo) throw Error('No Account and no Contact found');

        RpcClientFactory.getInstance().addTraceHeaders({
            jobNumber: jobInfo.jobNumber,
            accountNumber
        });
        return jobInfo;
    }
});
